import React, { useState } from 'react';

import AssetLibrary from '../../../shared/components/AssetLibrary/AssetLibrary';
import AssetService from '../../../shared/services/AssetService';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Unstable_Grid2';
import IconButton from '@mui/material/IconButton';
import ImageIcon from '@mui/icons-material/Image';
import { InlineQuillEdit } from '../InlineQuillEdit';
import LessonComponentService from '../../services/LessonComponentService';
import Radio from '@mui/material/Radio';
import { SelectionOption } from '../../models/LessonComponentContent';
import { cloneDeep } from 'lodash';
import { styled } from '@mui/system';
import styles from '../../styles/SelectionCard.module.css';
import { useTheme } from '@mui/material';

interface SelectionItemEditorProps {
  componentKey: number;
  hasImage?: boolean;
  isMultiple?: boolean;
  selectionCardItem?: SelectionOption;
  checked?: boolean;
  correctResponse?: string[];
  showBorder: boolean;
  onItemChange: (item: SelectionOption) => void;
  onSelectionChange?: (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => void;
}

const LabelFormControl = styled(FormControlLabel)(({ theme }) => ({
  '.MuiFormControlLabel-label': {
    width: '100%',
    flex: 1
  }
}));

export const SelectionItemEditor = (props: SelectionItemEditorProps) => {
  const muiTheme = useTheme();
  const primaryColor = muiTheme.palette.primary.main;
  const item = props.selectionCardItem;
  const [showAssetLibrary, setShowAssetLibrary] = useState(false);
  const hasValue = item.label || item.image;

  const handleChange = (label: string) => {
    if (label.trim()) {
      const copy = cloneDeep(item);
      props.onItemChange({ ...copy, label });
    }
  };

  const handleAssetSelect = (assetkey: string) => {
    const copy = cloneDeep(item);
    props.onItemChange({ ...copy, image: assetkey });
  };

  const hasBorder = props.showBorder && hasValue;

  if (!item) {
    return <></>;
  }

  return (
    <>
      <Grid
        className={`${styles.gridStretch} ${hasBorder && styles.labelBorder}`}
        style={{
          ...(hasBorder &&
            LessonComponentService.optionBorderColor(
              item.value,
              props.correctResponse,
              primaryColor
            ))
        }}
      >
        <LabelFormControl
          style={{
            width: '100%',
            paddingLeft: props.showBorder && !hasValue ? 12 : 0
          }}
          key={item.value}
          value={item.value}
          control={
            props.isMultiple ? (
              <Checkbox
                color="primary"
                disabled={!hasValue}
                checked={props.checked}
              />
            ) : (
              <Radio disabled={!hasValue} color="primary" />
            )
          }
          label={
            <Box
              className={`${styles.itemLabel} ${styles.muiFormControlLabel} bordered-editor`}
            >
              <InlineQuillEdit
                key={item.value}
                placeholder={
                  hasValue ? 'Click to edit option' : 'Click to add an option'
                }
                value={item.label}
                componentKey={props.componentKey}
                styleType={'label'}
                notAllowEmptyValue
                restrictEnterKey
                onChange={handleChange}
              />
            </Box>
          }
          onChange={props.onSelectionChange}
        />
      </Grid>
      {props.hasImage && (
        <Grid>
          {item?.image ? (
            <img
              className={styles.itemEditorThumbnail}
              src={AssetService.getAssetUrl(item.image)}
              onClick={() => setShowAssetLibrary(true)}
            />
          ) : (
            <IconButton
              className={!hasValue ? styles.blankItem : ''}
              size="small"
              onClick={() => setShowAssetLibrary(true)}
            >
              <ImageIcon />
            </IconButton>
          )}
        </Grid>
      )}
      {showAssetLibrary && (
        <AssetLibrary
          types={['image']}
          category="author"
          title="Select Image"
          selectedAssetkey={item?.image}
          onSelect={(assetkey) => handleAssetSelect(assetkey)}
          onClose={() => setShowAssetLibrary(false)}
        />
      )}
    </>
  );
};
